import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	FormControl,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	Stack,
	Typography,
} from "@mui/material";
import type { SelectChangeEvent } from "@mui/material";
import { ImportEnvironmentDto } from "Infrastructure/Api/Api";
import { useState } from "react";

interface ImportFormProps {
	environments: ImportEnvironmentDto[];
	isLoading: boolean;
	isExecuting: boolean;
	isBlocked?: boolean;
	noPermission?: boolean;
	onExecute: (environmentName: string, localization: string) => void;
}

export const ImportForm: React.FunctionComponent<ImportFormProps> = ({
	environments,
	isLoading,
	isExecuting,
	isBlocked = false,
	noPermission = false,
	onExecute,
}) => {
	const [selectedEnvironment, setSelectedEnvironment] = useState("");

	const handleEnvironmentChange = (event: SelectChangeEvent) => {
		setSelectedEnvironment(event.target.value);
	};

	const handleExecute = () => {
		const env = environments.find((e) => e.name === selectedEnvironment);
		if (env) {
			onExecute(env.name, env.localization);
		}
	};

	const selectedEnv = environments.find((e) => e.name === selectedEnvironment);
	const enabledEnvironments = environments.filter((e) => e.isEnabled);

	if (isLoading) {
		return (
			<Paper variant="outlined" sx={{ p: 3 }}>
				<Stack spacing={2} alignItems="center">
					<CircularProgress size={24} />
					<Typography variant="body2" color="text.secondary">
						Načítám prostředí...
					</Typography>
				</Stack>
			</Paper>
		);
	}

	if (enabledEnvironments.length === 0) {
		return (
			<Paper variant="outlined" sx={{ p: 3 }}>
				<Alert severity="warning">
					Nejsou nakonfigurována žádná prostředí pro import. Kontaktujte
					administrátora.
				</Alert>
			</Paper>
		);
	}

	return (
		<Paper variant="outlined" sx={{ p: 3 }}>
			<Stack spacing={3}>
				<Box>
					<Typography variant="subtitle1" fontWeight="medium" gutterBottom>
						Spustit import
					</Typography>
					<Typography variant="body2" color="text.secondary">
						Vyberte cílové prostředí a spusťte import číselníků do databáze.
					</Typography>
				</Box>

				<FormControl fullWidth size="small" disabled={isExecuting || isBlocked}>
					<InputLabel id="environment-select-label">Prostředí</InputLabel>
					<Select
						labelId="environment-select-label"
						id="environment-select"
						value={selectedEnvironment}
						label="Prostředí"
						onChange={handleEnvironmentChange}
						disabled={isExecuting || isBlocked}
					>
						{enabledEnvironments.map((env) => (
							<MenuItem key={env.name} value={env.name}>
								<Stack>
									<Typography variant="body2">{env.displayName}</Typography>
									{env.description && (
										<Typography variant="caption" color="text.secondary">
											{env.description}
										</Typography>
									)}
								</Stack>
							</MenuItem>
						))}
					</Select>
				</FormControl>

				{selectedEnv && (
					<Alert severity="info" sx={{ py: 0.5 }}>
						<Typography variant="body2">
							Lokalizace: <strong>{selectedEnv.localization}</strong>
						</Typography>
					</Alert>
				)}

				<Button
					variant="contained"
					color="primary"
					startIcon={
						isExecuting ? (
							<CircularProgress size={18} color="inherit" />
						) : (
							<PlayArrowIcon />
						)
					}
					onClick={handleExecute}
					disabled={!selectedEnvironment || isExecuting || isBlocked}
					fullWidth
				>
					{isExecuting
						? "Import běží..."
						: isBlocked
						  ? "Import blokován"
						  : "Spustit import"}
				</Button>

				{noPermission ? (
					<Alert severity="info" sx={{ py: 0.5 }}>
						<Typography variant="body2">
							Nemáte oprávnění spouštět importy. Kontaktujte administrátora.
						</Typography>
					</Alert>
				) : (
					<Alert severity="warning" sx={{ py: 0.5 }}>
						<Typography variant="body2">
							Import přepíše existující data v databázi. Tato akce je nevratná.
						</Typography>
					</Alert>
				)}
			</Stack>
		</Paper>
	);
};
